import type { RequestOptions } from "@@/plugin-request/request";
import type { RequestConfig } from "@umijs/max";
import { history, request as umiRequest } from "@umijs/max";
import { message, notification } from "antd";

// 全局开关：在 token 刷新期间抑制所有由 401 导致的 toast 提示
let _suppressAuthToasts = false;
const _origMessageError = message.error;
const _origMessageWarning = message.warning;
// 覆写 message.error/warning，在抑制开关打开时不显示
message.error = ((content: any, ...args: any[]) => {
  if (_suppressAuthToasts) return;
  return _origMessageError(content, ...args);
}) as typeof message.error;
message.warning = ((content: any, ...args: any[]) => {
  if (_suppressAuthToasts) return;
  return _origMessageWarning(content, ...args);
}) as typeof message.warning;

// 根据环境变量确定 API 基础地址
const API_BASE_URL =
  typeof REACT_APP_ENV !== 'undefined' && REACT_APP_ENV === 'production'
    ? 'https://bps.aigcempower.com'
    : 'https://bps.test.aigcempower.com';

// 错误处理方案： 错误类型
enum ErrorShowType {
  SILENT = 0,
  WARN_MESSAGE = 1,
  ERROR_MESSAGE = 2,
  NOTIFICATION = 3,
  REDIRECT = 9,
}
// 与后端约定的响应数据格式
interface ResponseStructure {
  success: boolean;
  code: number;
  message: string;
  data: any;
  request_id?: string;
  errorCode?: number;
  errorMessage?: string;
  showType?: ErrorShowType;
}

// 判断是否为认证相关错误（401 或 token 相关提示）
const isAuthError = (payload: any): boolean => {
  if (!payload) return false;
  const code = payload.code ?? payload.errorCode ?? payload.status;
  if (code === 401) return true;
  const msg = (payload.message || payload.errorMessage || '').toString();
  return (
    msg.includes('访问令牌已过期') ||
    msg.includes('token') ||
    msg.includes('令牌')
  );
};

// 单次刷新 Promise，避免并发刷新导致竞态
let refreshingPromise: Promise<boolean> | null = null;
const ensureRefresh = async (): Promise<boolean> => {
  if (!refreshingPromise) {
    // 开启抑制提示，防止并发请求期间弹出 401 提示
    _suppressAuthToasts = true;
    refreshingPromise = refreshAccessToken().finally(() => {
      refreshingPromise = null;
      _suppressAuthToasts = false;
    });
  }
  return refreshingPromise!;
};

// 刷新 access_token
const refreshAccessToken = async (): Promise<boolean> => {
  const refreshToken = localStorage.getItem("refresh_token");
  if (!refreshToken) {
    return false;
  }

  try {
    const res = await fetch(
      'https://acz.aigcempower.com/api/v1/auth/oidc/refresh-token',
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          client_id: '0692f27c-958d-751f-8000-2bbabede441c',
          client_secret: '994a588b1ea6170963cd23a10afe349a',
          refresh_token: refreshToken,
        }),
      },
    );
    if (!res.ok) {
      const errorText = await res.text().catch(() => '');
      return false;
    }

    const raw = await res.json().catch(() => null);
    if (!raw || typeof raw !== 'object') {
      return false;
    }

    const data = (raw as any).data || (raw as any);
    const accessToken = data.access_token as string | undefined;
    const newRefreshToken = data.refresh_token as string | undefined;

    if (accessToken) {
      localStorage.setItem('token', accessToken);
      try {
        if (typeof window !== 'undefined') {
          window.dispatchEvent(new CustomEvent('token:refreshed'));
        }
      } catch {
        // ignore
      }
    }

    if (newRefreshToken) {
      localStorage.setItem('refresh_token', newRefreshToken);
    }

    return !!accessToken;
  } catch (error) {
    console.error('[refreshAccessToken] 刷新异常:', error);
    return false;
  }
};

// 跳转到外部SSO登录
const redirectToExternalLogin = () => {
  // 清理本地登录信息
  localStorage.removeItem("token");
  sessionStorage.removeItem("token");
  localStorage.removeItem("user");
  sessionStorage.removeItem("user");
  localStorage.removeItem("refresh_token");

  const redirectUri = `${API_BASE_URL}/welcome`;
  window.location.href =
    `https://acz.aigcempower.com/login?redirect_uri=${redirectUri}`;
};

// 简化的重载函数：尝试 assign，失败回退到 reload
const performReload = () => {
  if (typeof window === 'undefined') return;
  try {
    window.location.assign(window.location.href);
  } catch {
    try {
      window.location.reload();
    } catch {
      // ignore
    }
  }
};

const showRefreshAndReload = () => {
  setTimeout(performReload, 300);
};

/**
 * @name 错误处理
 * pro 自带的错误处理， 可以在这里做自己的改动
 * @doc https://umijs.org/docs/max/request#配置
 */
export const errorConfig: RequestConfig = {
  // 错误处理： umi@3 的错误处理方案。
  errorConfig: {
    // 错误抛出
    errorThrower: (res) => {
      const {
        success,
        code,
        message,
        data,
        request_id,
        errorCode,
        errorMessage,
        showType,
      } = res as unknown as ResponseStructure;
      // 如果响应没有 success 字段（如登录接口），不进行错误处理
      if (success !== undefined && !success) {
        const error: any = new Error(message || errorMessage || "请求失败");
        error.name = "BizError";
        error.info = {
          errorCode: code || errorCode,
          errorMessage: message || errorMessage,
          showType,
          data,
          request_id,
        };
        throw error; // 抛出自制的错误
      }
    },
    // 错误接收及处理
    errorHandler: async (error: any, opts: any) => {
    try {
      // silent handler entry log removed
    } catch {}
      if (opts?.skipErrorHandler) {
        // skipErrorHandler
        throw error;
      }

      // 提前判断当前错误是否属于 auth（401 / token）相关，用于决定是否在后续展示提示
      let _topAuthPayload: any = null;
      try {
        if (error && error.name === 'BizError' && (error as any).info) {
          _topAuthPayload = (error as any).info;
        } else if (error && (error as any).response) {
          const _status = (error as any).response.status;
          const _respData = (error as any).response.data;
          _topAuthPayload = { code: _status, message: _respData?.message || _respData?.errorMessage || '' };
        }
      } catch {
        _topAuthPayload = null;
      }
      const _isAuthErrorTop = isAuthError(_topAuthPayload);

      // 如果当前地址上已经带有 code 参数（从 SSO 跳转回来），
      // 直接跳过所有错误处理，避免在回调过程中被重定向到 /user/login
      try {
        if (typeof window !== "undefined") {
          const url = new URL(window.location.href);
          const codeInUrl = url.searchParams.get("code");
          if (codeInUrl) {
            return;
          }
        }
      } catch {
        // 忽略 URL 解析异常
      }

      // 统一检查是否为认证错误（来自后端统一格式或 axios response）
      try {
        let authPayload: any = null;
        if (error && error.name === 'BizError' && (error as any).info) {
          authPayload = (error as any).info;
        } else if (error && (error as any).response) {
          const status = (error as any).response.status;
          const respData = (error as any).response.data;
          authPayload = { code: status, message: respData?.message || respData?.errorMessage || '' };
        }

        if (isAuthError(authPayload)) {
          // 如果已经重试过则不要立即跳登录，交给调用方处理（避免误跳）
          if ((opts as any)?._retry || ((error as any)?.config && (error as any).config._retry)) {
            throw error;
          }

          // 使用单次刷新队列，避免并发刷新导致竞态
          const refreshSuccess = await ensureRefresh();
          if (refreshSuccess) {
            // 无感重试：使用新的 token 自动重发原始请求并把结果返回给调用方
            const token = localStorage.getItem('token') || sessionStorage.getItem('token');
            try {
              // BizError (统一格式) 的原请求信息通常在 opts 中（umi 的 request）
              if (error && error.name === 'BizError' && opts && (opts as any).url) {
                (opts as any)._retry = true;
                const retryHeaders = {
                  ...(opts.headers || {}),
                  ...(token ? { Authorization: `Bearer ${token}` } : {}),
                };
                return umiRequest((opts as any).url, {
                  ...(opts || {}),
                  headers: retryHeaders,
                  method: (opts as any).method,
                  params: (opts as any).params,
                  data: (opts as any).data,
                }).then((r) => {
                  try {
                    // message.success('重新加载数据成功');
                  } catch {}
                  return r;
                });
              }

              // Axios error 的原始请求在 error.response.config 中
              if (error && (error as any).response && (error as any).response.config) {
                const originalRequest: any = (error as any).response.config;
                originalRequest._retry = true;
                const retryHeaders = {
                  ...(originalRequest.headers || {}),
                  ...(token ? { Authorization: `Bearer ${token}` } : {}),
                };
                return umiRequest(originalRequest.url, {
                  method: originalRequest.method,
                  params: originalRequest.params,
                  data: originalRequest.data,
                  headers: retryHeaders,
                }).then((r) => {
                  try {
                    // message.success('重新加载数据成功');
                  } catch {}
                  return r;
                });
              }

              // 如果无法识别原始请求，作为兜底仍然做一次页面刷新（极少数情况）
              showRefreshAndReload();
              return;
            } catch (e) {
              // 重试失败则跳回外部登录
              redirectToExternalLogin();
              return;
            }
          }

          redirectToExternalLogin();
          return;
        }
      } catch {
        // 忽略 auth 检查异常，继续后续错误处理
      }

      // 我们的 errorThrower 抛出的错误。
      if (error.name === "BizError") {
        const errorInfo: ResponseStructure | undefined = error.info;
        if (errorInfo) {
          const { errorMessage, errorCode } = errorInfo;

          // 认证错误已经在上层统一处理，这里只做业务错误展示

          switch (errorInfo.showType) {
            case ErrorShowType.SILENT:
              // do nothing
              break;
            case ErrorShowType.WARN_MESSAGE:
              if (!_isAuthErrorTop) message.warning(errorMessage);
              break;
            case ErrorShowType.ERROR_MESSAGE:
              if (!_isAuthErrorTop) message.error(errorMessage);
              break;
            case ErrorShowType.NOTIFICATION:
              if (!_isAuthErrorTop)
                notification.open({
                  description: errorMessage,
                  message: errorCode,
                });
              break;
            case ErrorShowType.REDIRECT:
              // TODO: redirect
              break;
            default:
              if (!_isAuthErrorTop) message.error(errorMessage);
          }
        }
      } else if (error.response) {
        // Axios 的错误
        // 请求成功发出且服务器也响应了状态码，但状态代码超出了 2xx 的范围
        const status = error.response.status;
        const responseData = error.response.data;
        const originalRequest: any = (error as any).config || (error.response as any).config;

        // 认证错误已经在上层统一处理，这里只做其他错误展示

        if (!_isAuthErrorTop) {
          if (responseData?.message) {
            message.error(responseData.message);
          } else {
            message.error(`请求失败，状态码: ${status}`);
          }
        }
      } else if (error.request) {
        if (!_isAuthErrorTop) message.error("None response! Please retry.");
      } else {
        if (!_isAuthErrorTop) message.error("Request error, please retry.");
      }
    },
  },

  // 请求拦截器
  requestInterceptors: [
    (config: RequestOptions) => {
      // 添加 Authorization Bearer token
      const token =
        localStorage.getItem("token") || sessionStorage.getItem("token");
      if (token) {
        config.headers = {
          ...config.headers,
          Authorization: `Bearer ${token}`,
        };
      }
      return config;
    },
  ],

  // 响应拦截器
  responseInterceptors: [
    (response) => {
      // 401 状态码不应该进入响应拦截器，应该直接进入错误处理
      // 但为了安全，这里也检查一下
      if (response.status === 401) {
        // 401 响应应该被当作错误处理，不在这里处理
        return Promise.reject({
          response: {
            status: 401,
            data: response.data,
          },
        });
      }

      // 拦截响应数据，进行个性化处理
      // 注意：response 是 axios 响应对象，实际数据在 response.data 中
      const { data } = response as any;

      // 处理统一响应格式 { success, code, message, data, request_id }
      // 如果响应格式符合规范，提取 data 字段作为实际数据
      if (
        data &&
        typeof data === "object" &&
        "success" in data &&
        "code" in data &&
        "data" in data
      ) {
        // 如果 success 为 false 且 code 为 401，应该被当作错误处理
        if (data.success === false && (data.code === 401 || data.errorCode === 401)) {
          return Promise.reject({
            name: "BizError",
            info: {
              errorCode: data.code || data.errorCode,
              errorMessage: data.message || data.errorMessage,
              showType: data.showType,
              data: data.data,
              request_id: data.request_id,
            },
          });
        }

        // 将实际数据提取到 response.data，同时保留原始响应信息用于错误处理
        return {
          ...response,
          data: {
            ...data,
            // 保留 success 字段用于 errorThrower 错误处理
            success: data.success,
            // 将实际数据放在顶层，方便使用
            ...(typeof data.data === "object" && data.data !== null
              ? data.data
              : { data: data.data }),
          },
        };
      }

      return response;
    },
  ],
};
